import { useState } from 'react';
import { Upload, Sparkles, Link as LinkIcon, Check, X, Loader2, Search } from 'lucide-react';
import { useQuickEdit } from '@/hooks/useQuickEdit';
import Modal from '@/components/ui/Modal';
import ImageUploader from '@/components/admin/ImageUploader';
import AiForgeButton from '@/components/admin/AiForgeButton';
import StockPhotoPicker from '@/components/admin/StockPhotoPicker';
import { assetTarget, assetPrompt } from '@/config/asset-specs';
import { useUIStore } from '@/stores/ui-store';

interface QuickEditImageProps {
  src: string | undefined;
  onSave: (url: string) => Promise<unknown>;
  label: string;
  targetWidth?: number;
  targetHeight?: number;
  /** Slot key in config/asset-specs.ts — supplies crop ratio and AI prompt. */
  specKey?: string;
  children: React.ReactNode;
}

export default function QuickEditImage({
  src,
  onSave,
  label,
  targetWidth,
  targetHeight,
  specKey,
  children,
}: QuickEditImageProps) {
  const { isActive } = useQuickEdit();
  const [mode, setMode] = useState<'idle' | 'upload' | 'ai' | 'url' | 'stock'>('idle');
  const [urlInput, setUrlInput] = useState('');
  const [saving, setSaving] = useState(false);

  if (!isActive) return <>{children}</>;

  const spec = specKey ? assetTarget(specKey) : undefined;
  const cropWidth = targetWidth ?? spec?.width ?? 1200;
  const cropHeight = targetHeight ?? spec?.height ?? 900;
  const aiPrompt = specKey ? assetPrompt(specKey) : undefined;

  async function handleSave(url: string) {
    setSaving(true);
    try {
      await onSave(url);
      setMode('idle');
    } catch (err) {
      // Keep the modal open so the choice is not lost, but say why — a silent
      // failure here looks identical to a slow upload.
      useUIStore
        .getState()
        .addToast(
          err instanceof Error && err.message ? `Could not save: ${err.message}` : 'Could not save that image',
          'error',
        );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="group/qeimg relative">
      {children}
      {/* Hover overlay */}
      <div
        className={`absolute inset-0 z-10 flex items-center justify-center gap-3 transition-opacity ${
          // `hover-reveal-qeimg` is what makes this visible on touch and on
          // keyboard focus — group-hover alone never fires on a tablet, so the
          // overlay was invisible (though still clickable) on every touch
          // device. See index.css.
          !src
            ? 'opacity-100 bg-black/20'
            : 'opacity-0 group-hover/qeimg:opacity-100 group-focus-within/qeimg:opacity-100 hover-reveal-qeimg bg-black/40'
        }`}
        aria-label={label}
      >
        <button
          onClick={() => setMode('upload')}
          className="flex items-center gap-1.5 rounded-lg bg-white/90 px-3 py-2 text-sm font-medium text-gray-800 shadow-lg hover:bg-white transition-colors"
          aria-label="Upload image"
          type="button"
        >
          <Upload className="h-4 w-4" aria-hidden="true" />
          Upload
        </button>
        <button
          onClick={() => setMode('ai')}
          className="flex items-center gap-1.5 rounded-lg bg-blue-600/90 px-3 py-2 text-sm font-medium text-white shadow-lg hover:bg-blue-700 transition-colors"
          aria-label="AI Generate image"
          type="button"
        >
          <Sparkles className="h-4 w-4" aria-hidden="true" />
          AI Generate
        </button>
        <button
          onClick={() => setMode('stock')}
          className="flex items-center gap-1.5 rounded-lg bg-white/90 px-3 py-2 text-sm font-medium text-gray-800 shadow-lg hover:bg-white transition-colors"
          aria-label="Find a stock photo"
          type="button"
        >
          <Search className="h-4 w-4" aria-hidden="true" />
          Stock
        </button>
        <button
          onClick={() => setMode('url')}
          className="flex items-center gap-1.5 rounded-lg bg-white/90 px-3 py-2 text-sm font-medium text-gray-800 shadow-lg hover:bg-white transition-colors"
          aria-label="Paste image URL"
          type="button"
        >
          <LinkIcon className="h-4 w-4" aria-hidden="true" />
          URL
        </button>
      </div>

      <StockPhotoPicker
        isOpen={mode === 'stock'}
        onClose={() => setMode('idle')}
        folder="homepage"
        targetWidth={cropWidth}
        targetHeight={cropHeight}
        initialQuery={label.toLowerCase()}
        onPick={({ url }) => handleSave(url)}
      />

      {/* Upload modal */}
      <Modal
        isOpen={mode === 'upload'}
        onClose={() => setMode('idle')}
        title="Upload Image"
      >
        <div className="p-4">
          <ImageUploader
            label={label}
            value={src || ''}
            onChange={(url) => handleSave(url)}
            targetWidth={cropWidth}
            targetHeight={cropHeight}
          />
        </div>
      </Modal>

      {/* AI Image Studio */}
      <AiForgeButton
        isOpen={mode === 'ai'}
        onClose={() => setMode('idle')}
        context="homepage"
        contextName={label.toLowerCase()}
        initialPrompt={aiPrompt}
        onImageGenerated={({ url }) => handleSave(url)}
      />

      {/* URL input modal */}
      <Modal
        isOpen={mode === 'url'}
        onClose={() => { setMode('idle'); setUrlInput(''); }}
        title="Paste Image URL"
      >
        <div className="p-4 space-y-4">
          <input
            type="url"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            placeholder="https://example.com/image.jpg"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-label="Image URL"
          />
          <div className="flex justify-end gap-2">
            <button
              onClick={() => { setMode('idle'); setUrlInput(''); }}
              className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
              type="button"
            >
              <X className="h-4 w-4 inline mr-1" aria-hidden="true" />
              Cancel
            </button>
            <button
              onClick={() => urlInput.trim() && handleSave(urlInput.trim())}
              disabled={!urlInput.trim() || saving}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              type="button"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 inline mr-1 animate-spin" aria-hidden="true" />
              ) : (
                <Check className="h-4 w-4 inline mr-1" aria-hidden="true" />
              )}
              Save
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
